import { ArrowLeft, MoreVertical, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/ui/base/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/ui/base/dropdown-menu";

export interface MobileNavLink {
  /** Texto exibido no item do menu. */
  label: string;
  /** Destino (`react-router-dom` Link). */
  href: string;
  /** Ícone opcional (lucide). */
  icon?: LucideIcon;
}

interface MobileNavMenuProps {
  /** Rótulo curto no centro (ex.: "Maio/2026 · PF"). Opcional. */
  title?: string;
  /** Caminho do "Voltar". */
  backTo: string;
  /** Rótulo do botão Voltar (default "Voltar"). */
  backLabel?: string;
  /**
   * Links secundários exibidos no dropdown `⋯`. Quando vazio, o `⋯` é
   * suprimido — vira apenas `[← Voltar] [título]`.
   */
  links?: MobileNavLink[];
  /** Rótulo da seção do dropdown (default "Navegação"). */
  menuLabel?: string;
  /** Aria-label do trigger `⋯` (default "Mais ações"). */
  menuAriaLabel?: string;
}

/**
 * Toolbar compacta `[← Voltar] [título] [⋯ menu]` para telas de detalhe em
 * mobile (`sm:hidden`). Touch targets ≥ 44px. Substitui filas de 2-3 botões
 * inline que quebram em múltiplas linhas em telas estreitas.
 *
 * Uso direto em telas com header customizado, ou via prop `mobileNav` do
 * `<DetailHeader />`.
 */
export function MobileNavMenu({
  title,
  backTo,
  backLabel = "Voltar",
  links,
  menuLabel = "Navegação",
  menuAriaLabel = "Mais ações",
}: MobileNavMenuProps) {
  const hasLinks = !!links && links.length > 0;

  return (
    <div className="flex items-center justify-between gap-2 -mx-1 -mt-1 mb-1 sm:hidden">
      <Button asChild variant="ghost" size="sm" className="h-10 px-2 -ml-1" aria-label={backLabel}>
        <Link to={backTo}>
          <ArrowLeft className="h-4 w-4" />
          <span className="ml-1">{backLabel}</span>
        </Link>
      </Button>

      {title && (
        <div
          className="min-w-0 flex-1 text-center text-xs font-medium text-muted-foreground truncate"
          aria-hidden="true"
        >
          {title}
        </div>
      )}

      {hasLinks ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 -mr-1"
              aria-label={menuAriaLabel}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {links!.map((l) => {
              const Icon = l.icon;
              return (
                <DropdownMenuItem key={l.href + l.label} asChild>
                  <Link to={l.href}>
                    {Icon && <Icon className="h-4 w-4 mr-2" />}
                    {l.label}
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        /* Espaçador para manter o título centralizado quando não há menu. */
        <div className="w-10 h-10 -mr-1" aria-hidden="true" />
      )}
    </div>
  );
}
